/**
 * CAL-001 · Operations Administration → Blackout periods.
 *
 * Combines the read-only UK public-holiday preview for the configured
 * region with the manual-blackout CRUD table Operations uses to add
 * one-off closure windows (training days, tenant events, etc). The
 * manual refresh button forces a GOV.UK re-fetch — bypassing the
 * once-per-quarter cache — and is the ONLY path that surfaces the
 * network round-trip to end users.
 *
 * Individual engineer holidays remain on the engineer roster and are
 * intentionally not managed here (`FIELD-001C1` scope).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarOff, RefreshCw, Trash2, Plus, X } from "lucide-react";
import OperationsShell from "@/components/operations/OperationsShell";
import BackButton from "@/components/BackButton";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const REGION_LABEL = {
    "england-and-wales": "England & Wales",
    "scotland": "Scotland",
    "northern-ireland": "Northern Ireland",
};

function ukDate(iso) {
    if (!iso) return "";
    try {
        const [y, m, d] = String(iso).split("-").map((x) => parseInt(x, 10));
        return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", {
            weekday: "short", day: "numeric", month: "short", year: "numeric",
        });
    } catch { return iso; }
}

export default function OperationsBlackouts() {
    const { hasPermission } = useAuth();
    const canManage = hasPermission("manage_calendar") || hasPermission("manage_system_settings");
    const [settings, setSettings] = useState(null);
    const [holidays, setHolidays] = useState({ events: [], cached_at: null, stale: false });
    const [blackouts, setBlackouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [savingRegion, setSavingRegion] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ name: "", start_date: "", end_date: "" });
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true); setError("");
        try {
            const [s, h, bs] = await Promise.all([
                api.getOrganisationSettings(),
                api.getBankHolidays(),
                api.adminListBlackouts(),
            ]);
            setSettings(s);
            setHolidays(h);
            setBlackouts(bs);
        } catch (e) {
            setError(formatApiError(e?.response?.data?.detail) || "Failed to load blackouts");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onChangeRegion = async (region) => {
        setSavingRegion(true);
        try {
            const s = await api.patchOrganisationSettings({ bank_holiday_region: region });
            setSettings((prev) => ({ ...prev, ...s }));
            const h = await api.getBankHolidays();
            setHolidays(h);
            toast.success(`Region updated to ${REGION_LABEL[region] || region}`);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to update region");
        } finally { setSavingRegion(false); }
    };

    const onRefreshHolidays = async () => {
        setRefreshing(true);
        try {
            const res = await api.adminRefreshBankHolidays();
            setHolidays(res);
            if (res.stale) toast.warning("GOV.UK is unreachable — showing last successful cache");
            else toast.success("Bank holidays refreshed");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to refresh");
        } finally { setRefreshing(false); }
    };

    const onCreate = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.start_date) return;
        setCreating(true);
        try {
            const row = await api.adminCreateBlackout({
                name: form.name.trim(),
                start_date: form.start_date,
                end_date: form.end_date || form.start_date,
            });
            setBlackouts((prev) => [...(prev || []), row].sort((a, b) => a.start_date.localeCompare(b.start_date)));
            setForm({ name: "", start_date: "", end_date: "" });
            toast.success("Blackout added");
        } catch (e2) {
            toast.error(formatApiError(e2?.response?.data?.detail) || "Failed to add blackout");
        } finally { setCreating(false); }
    };

    const onDelete = async (id) => {
        try {
            await api.adminDeleteBlackout(id);
            setBlackouts((prev) => (prev || []).filter((b) => b.id !== id));
            toast.success("Blackout removed");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to remove");
        }
    };

    const upcomingHolidays = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        return ((holidays && holidays.events) || []).filter((e) => e.date >= today).slice(0, 12);
    }, [holidays]);

    if (!canManage) {
        return (
            <OperationsShell>
                <div className="mx-auto max-w-4xl px-4 md:px-8 pt-6 md:pt-10 pb-16">
                    <BackButton fallbackHref="/operations/administration" />
                    <div className="mt-6 rounded-xl border border-[#E5E7EB] bg-white p-8 text-center" data-testid="blackouts-forbidden">
                        You do not have permission to manage blackout periods.
                    </div>
                </div>
            </OperationsShell>
        );
    }

    return (
        <OperationsShell>
            <div className="mx-auto max-w-5xl px-4 md:px-8 pt-6 md:pt-10 pb-16" data-testid="ops-blackouts-page">
                <BackButton fallbackHref="/operations/administration" testid="ops-blackouts-back" className="mb-4" />
                <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-[#E1E6F0] text-[#131B2F] flex items-center justify-center shrink-0">
                        <CalendarOff size={22} strokeWidth={1.75} />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#4B5565] font-medium">Operations · Administration</div>
                        <h1 className="text-2xl md:text-3xl text-[#131B2F] font-semibold">Blackout periods</h1>
                        <p className="mt-2 text-sm text-[#4B5565] max-w-2xl">
                            UK bank holidays and manual blackout periods appear as
                            non-blocking warnings across tasks, appointments and site
                            survey scheduling. Weekends stay schedulable.
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-3 rounded-md border border-[#DA3535]/30 bg-[#FEF3F3] text-sm text-[#991B1B]" data-testid="blackouts-error">
                        {error}
                    </div>
                )}

                {/* Region + refresh */}
                <div className="rounded-xl border border-[#E5E7EB] bg-white p-5 mb-6" data-testid="blackouts-region-card">
                    <div className="flex flex-wrap items-center gap-3">
                        <div>
                            <div className="text-xs uppercase tracking-[0.06em] text-[#4B5565]">UK public holiday region</div>
                            <div className="text-[15px] font-semibold text-[#131B2F]">
                                {REGION_LABEL[settings?.bank_holiday_region] || "England & Wales"}
                            </div>
                        </div>
                        <div className="flex-1" />
                        <select
                            value={settings?.bank_holiday_region || "england-and-wales"}
                            onChange={(e) => onChangeRegion(e.target.value)}
                            disabled={savingRegion || loading}
                            data-testid="blackouts-region-select"
                            className="h-9 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm"
                        >
                            {(settings?.supported_regions || Object.keys(REGION_LABEL)).map((r) => (
                                <option key={r} value={r}>{REGION_LABEL[r] || r}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={onRefreshHolidays}
                            disabled={refreshing}
                            data-testid="blackouts-refresh"
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm hover:bg-[#F5F5F7]"
                        >
                            <RefreshCw size={14} strokeWidth={1.75} className={refreshing ? "animate-spin" : ""} />
                            {refreshing ? "Refreshing…" : "Refresh from GOV.UK"}
                        </button>
                    </div>
                    <div className="mt-3 text-xs text-[#4B5565]">
                        {(holidays && holidays.cached_at) ? (
                            <>Last successful sync: {new Date(holidays.cached_at).toLocaleString()}</>
                        ) : "No successful sync yet."}
                        {(holidays && holidays.stale) && (
                            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#FEF3C7] text-[#B45309] px-2 py-0.5" data-testid="blackouts-stale-banner">
                                Stale — GOV.UK unreachable. Calendar continues to work using the last cached data.
                            </span>
                        )}
                    </div>
                </div>

                {/* Upcoming public holidays preview */}
                <div className="rounded-xl border border-[#E5E7EB] bg-white p-5 mb-6" data-testid="blackouts-public-list">
                    <div className="text-xs uppercase tracking-[0.06em] text-[#4B5565] mb-2">Upcoming UK public holidays</div>
                    {upcomingHolidays.length === 0 ? (
                        <div className="text-sm text-[#4B5565]">None found in the current cache.</div>
                    ) : (
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {upcomingHolidays.map((h) => (
                                <li key={h.date} className="flex items-center justify-between rounded-md bg-[#F9FAFB] px-3 py-2 text-sm">
                                    <span className="text-[#131B2F] font-medium">{h.title}</span>
                                    <span className="text-[#4B5565] text-xs">{ukDate(h.date)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Manual blackouts */}
                <div className="rounded-xl border border-[#E5E7EB] bg-white p-5" data-testid="blackouts-manual-card">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="text-[15px] font-semibold text-[#131B2F]">Manual blackout periods</div>
                        <span className="text-xs text-[#4B5565]">— shown alongside UK holidays on every calendar view</span>
                    </div>

                    <form onSubmit={onCreate} className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4" data-testid="blackouts-create-form">
                        <input
                            type="text"
                            placeholder="Name (e.g. Company AGM)"
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            data-testid="blackouts-form-name"
                            className="h-9 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm md:col-span-2"
                        />
                        <input
                            type="date"
                            value={form.start_date}
                            onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value, end_date: f.end_date || e.target.value }))}
                            data-testid="blackouts-form-start"
                            className="h-9 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm"
                        />
                        <input
                            type="date"
                            value={form.end_date}
                            onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                            data-testid="blackouts-form-end"
                            className="h-9 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm"
                        />
                        <div className="md:col-span-4 flex items-center justify-end gap-2">
                            {form.name && (
                                <button
                                    type="button"
                                    onClick={() => setForm({ name: "", start_date: "", end_date: "" })}
                                    data-testid="blackouts-form-cancel"
                                    className="h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm hover:bg-[#F5F5F7]"
                                >
                                    <X size={14} strokeWidth={1.75} className="inline mr-1" /> Cancel
                                </button>
                            )}
                            <button
                                type="submit"
                                disabled={!form.name.trim() || !form.start_date || creating}
                                data-testid="blackouts-form-submit"
                                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#131B2F] text-white text-sm font-medium disabled:opacity-60"
                            >
                                <Plus size={14} strokeWidth={1.75} /> {creating ? "Adding…" : "Add blackout"}
                            </button>
                        </div>
                    </form>

                    {(blackouts || []).length === 0 ? (
                        <div className="text-sm text-[#4B5565]" data-testid="blackouts-empty">No manual blackouts configured.</div>
                    ) : (
                        <table className="w-full text-sm" data-testid="blackouts-table">
                            <thead className="text-[11px] uppercase tracking-[0.06em] text-[#4B5565] border-b border-[#F3F4F6]">
                                <tr>
                                    <th className="py-2 text-left">Name</th>
                                    <th className="py-2 text-left">Start</th>
                                    <th className="py-2 text-left">End</th>
                                    <th className="py-2" />
                                </tr>
                            </thead>
                            <tbody>
                                {(blackouts || []).filter(Boolean).map((b) => (
                                    <tr key={b.id} className="border-b border-[#F3F4F6]" data-testid={`blackout-row-${b.id}`}>
                                        <td className="py-2 text-[#131B2F]">{b.name}</td>
                                        <td className="py-2 text-[#4B5565]">{ukDate(b.start_date)}</td>
                                        <td className="py-2 text-[#4B5565]">{ukDate(b.end_date)}</td>
                                        <td className="py-2 text-right">
                                            <button
                                                type="button"
                                                onClick={() => onDelete(b.id)}
                                                data-testid={`blackout-delete-${b.id}`}
                                                className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md border border-[#E5E7EB] bg-white text-xs hover:bg-[#F5F5F7]"
                                            >
                                                <Trash2 size={12} strokeWidth={1.75} /> Remove
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </OperationsShell>
    );
}
